import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Message, User, Poll, PollOption, ChatCategory } from '../types/community';

export function useCommunity() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [pinnedMessages, setPinnedMessages] = useState<Message[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [categories, setCategories] = useState<ChatCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const PAGE_SIZE = 50;
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Keep a reference to the general category ID for fallback
  const generalCategoryIdRef = useRef<string | null>(null);
  
  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('chat_categories')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      const categories = data || [];
      setCategories(categories);
      
      // Find and select the General category
      const generalCategory = categories.find(c => 
        c.name.toLowerCase().includes('general')
      );
      
      if (generalCategory) {
        // Store general category ID as fallback
        generalCategoryIdRef.current = generalCategory.id;
        
        // If no category is selected, default to general
        if (!selectedCategory) {
          setSelectedCategory(generalCategory.id);
        }
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchMessages = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Authentication required');

      // Reset state for fresh load
      setMessages([]);

      // Get user's likes
      const { data: userLikes } = await supabase
        .from('message_likes')
        .select('message_id')
        .eq('user_id', session.user.id);

      const userLikedMessages = new Set(userLikes?.map(like => like.message_id) || []);

      const selection = `
        *,
        profiles:user_id (
          email,
          display_name,
          avatar_url,
          is_admin
        ),
        comments:comments (
          id,
          content,
          created_at,
          profiles:user_id (
            email,
            display_name,
            avatar_url
          )
        )
      `;

      // Determine which category to filter by: selected or fallback to General
      const categoryFilter = selectedCategory ?? generalCategoryIdRef.current;

      // Fetch all pinned messages (not paginated)
      let pinnedQuery = supabase
        .from('messages')
        .select(selection)
        .eq('is_pinned', true)
        .order('created_at', { ascending: false });
      if (categoryFilter) {
        pinnedQuery = pinnedQuery.eq('category_id', categoryFilter);
      }
      const { data: pinnedData, error: pinnedError } = await pinnedQuery;
      if (pinnedError) throw pinnedError;

      // Fetch latest PAGE_SIZE non-pinned messages
      let nonPinnedQuery = supabase
        .from('messages')
        .select(selection)
        .eq('is_pinned', false)
        .order('created_at', { ascending: false })
        .range(0, PAGE_SIZE - 1);
      if (categoryFilter) {
        nonPinnedQuery = nonPinnedQuery.eq('category_id', categoryFilter);
      }
      const { data: nonPinnedData, error: nonPinnedError } = await nonPinnedQuery;
      if (nonPinnedError) throw nonPinnedError;

      const pinned = (pinnedData || []).map(message => ({
        ...message,
        user_has_liked: userLikedMessages.has(message.id)
      }));
      const firstPage = (nonPinnedData || []).map(message => ({
        ...message,
        user_has_liked: userLikedMessages.has(message.id)
      }));

      setPinnedMessages(pinned);
      setMessages(firstPage);
      setHasMore((nonPinnedData?.length || 0) === PAGE_SIZE);
      setError(null);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
      setError('Connection error. Retrying...');
      retryFetchMessages();
    } finally {
      setLoading(false);
    }
  };
  const retryFetchMessages = () => {
    retryTimeoutRef.current = setTimeout(() => {
      fetchMessages();
    }, 5000);
  };

  const loadMoreMessages = async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      const { data: { session } } = await supabase.auth.getSession();
      let userLikedMessages = new Set<string>();
      if (session) {
        const { data: userLikes } = await supabase
          .from('message_likes')
          .select('message_id')
          .eq('user_id', session.user.id);
        userLikedMessages = new Set(userLikes?.map(like => like.message_id) || []);
      }

      const selection = `
        *,
        profiles:user_id (
          email,
          display_name,
          avatar_url,
          is_admin
        ),
        comments:comments (
          id,
          content,
          created_at,
          profiles:user_id (
            email,
            display_name,
            avatar_url
          )
        )
      `;

      const offset = messages.length;
      let query = supabase
        .from('messages')
        .select(selection)
        .eq('is_pinned', false)
        .order('created_at', { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      if (selectedCategory) {
        query = query.eq('category_id', selectedCategory);
      }
      const { data, error } = await query;
      if (error) throw error;

      const nextPage = (data || []).map(message => ({
        ...message,
        user_has_liked: userLikedMessages.has(message.id)
      }));
      setMessages(prev => [...prev, ...nextPage]);
      setHasMore((data?.length || 0) === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more messages:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const togglePin = async (messageId: string, currentPinned: boolean) => {
    console.log('togglePin called with:', { messageId, currentPinned });
    
    // Check if user is admin before proceeding
    if (!isAdmin) {
      console.warn('Non-admin user attempted to toggle pin');
      setError('Only administrators can pin/unpin messages');
      return;
    }
    
    try {
      console.log('Sending pin update to Supabase...');
      const { data, error } = await supabase
        .from('messages')
        .update({ is_pinned: !currentPinned })
        .eq('id', messageId);

      console.log('Supabase response:', { data, error });
      
      if (error) throw error;
      // Fetch the updated message and move it between lists without resetting pagination
      const selection = `
        *,
        profiles:user_id (
          email,
          display_name,
          avatar_url,
          is_admin
        ),
        comments:comments (
          id,
          content,
          created_at,
          profiles:user_id (
            email,
            display_name,
            avatar_url
          )
        )
      `;
      const { data: updatedMessage } = await supabase
        .from('messages')
        .select(selection)
        .eq('id', messageId)
        .single();

      if (updatedMessage) {
        const existing = [...messages, ...pinnedMessages].find(m => m.id === messageId);
        const messageWithLike = { ...updatedMessage, user_has_liked: existing?.user_has_liked ?? false } as any;
        if (messageWithLike.is_pinned) {
          setMessages(prev => prev.filter(m => m.id !== messageId));
          setPinnedMessages(prev => [messageWithLike, ...prev.filter(m => m.id !== messageId)]);
        } else {
          setPinnedMessages(prev => prev.filter(m => m.id !== messageId));
          setMessages(prev => [messageWithLike, ...prev.filter(m => m.id !== messageId)]);
        }
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
      setError('Failed to update pin status');
    }
  };

  const deleteMessage = async (messageId: string, userId: string) => {
    try {
      if (!currentUser) {
        setError('You must be logged in to delete messages');
        return;
      }

      if (!isAdmin && currentUser.id !== userId) {
        setError('You can only delete your own messages');
        return;
      }

      const { error: deleteError } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId)
        .single();

      if (deleteError) throw deleteError;

      setMessages(prev => prev.filter(m => m.id !== messageId));
      setPinnedMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (err) {
      console.error('Error deleting message:', err);
      setError('Failed to delete message');
    }
  };

  const addMessage = async (content: string, categoryOrImage?: string | File) => {
    // Determine if the second parameter is a category ID (string) or an image file
    const isCategory = typeof categoryOrImage === 'string';
    
    // Handle category selection logic
    let category = isCategory ? categoryOrImage : selectedCategory;
    
    // If category is empty string, use the general category as fallback
    // instead of setting to null
    if (category === '') {
      category = generalCategoryIdRef.current || selectedCategory;
      console.log('Using fallback category:', category);
    }
    
    const image = !isCategory ? categoryOrImage : undefined;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      let imageUrl = '';
      let like_count = 0;
      if (image) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('message-images')
          .upload(fileName, image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('message-images')
          .getPublicUrl(fileName);
        
        imageUrl = publicUrl;
      }

      

      const { data: newMessage, error } = await supabase
        .from('messages')
        .insert({
          content,
          image_url: imageUrl || null,
          user_id: user.id,
          like_count,
          category_id: category || generalCategoryIdRef.current, // Use general category as fallback
        })
        .select(`
          *,
          profiles:user_id (
            email,
            display_name,
            avatar_url,
            is_admin
          ),
          comments:comments (
            id,
            content,
            created_at,
            profiles:user_id (
              email,
              display_name,
              avatar_url
            )
          )
        `)
        .single();

      if (error) throw error;

      // Add message to UI - ensure we handle null category cases properly
      // Case 1: No category filter is active - show all messages
      // Case 2: Message has the same category as the filter
      // Case 3: Message has null category AND selectedCategory is empty string
      if (!selectedCategory || 
          newMessage.category_id === selectedCategory || 
          (newMessage.category_id === null && !selectedCategory)) {
        setMessages(prev => [newMessage, ...prev]);
        
        // Log success for debugging
        console.log('Message added successfully:', newMessage);
      } else {
        // Log why message wasn't added to UI for debugging
        console.log('Message not shown in current view - category mismatch:', 
          { messageCategory: newMessage.category_id, selectedCategory });
      }
      
      return true;
    } catch (err) {
      console.error('Error adding message:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message');
      return false;
    }
  };

  const addComment = async (messageId: string, content: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('comments')
        .insert({
          message_id: messageId,
          user_id: user.id,
          content: content.trim(),
        });

      if (error) throw error;
      // Refresh just this message to get latest comments without resetting pagination
      const selection = `
        *,
        profiles:user_id (
          email,
          display_name,
          avatar_url,
          is_admin
        ),
        comments:comments (
          id,
          content,
          created_at,
          profiles:user_id (
            email,
            display_name,
            avatar_url
          )
        )
      `;
      const { data: updatedMessage } = await supabase
        .from('messages')
        .select(selection)
        .eq('id', messageId)
        .single();
      if (updatedMessage) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...updatedMessage, user_has_liked: m.user_has_liked } as any : m));
        setPinnedMessages(prev => prev.map(m => m.id === messageId ? { ...updatedMessage, user_has_liked: m.user_has_liked } as any : m));
      }
      return true;
    } catch (err) {
      console.error('Error adding comment:', err);
      return false;
    }
  };

  const fetchPolls = async () => {
    try {
      let query = supabase
        .from('polls')
        .select(`
          *,
          profiles:user_id (
            email,
            display_name,
            avatar_url,
            is_admin
          )
        `);

      // Filter by category if selected
      if (selectedCategory) {
        query = query.eq('category_id', selectedCategory);
      }

      const { data: pollsData, error: pollsError } = await query.order('created_at', { ascending: false });


      if (pollsError) throw pollsError;

      // Fetch options for all polls
      const { data: optionsData } = await supabase
        .from('poll_options')
        .select('*')
        .in('poll_id', pollsData?.map(p => p.id) || []);

      // Fetch votes for all options
      const { data: votesData } = await supabase
        .from('poll_votes')
        .select('*')
        .in('option_id', optionsData?.map(o => o.id) || []);

      // Process the data
      const processedPolls = pollsData.map(poll => {
        const pollOptions = optionsData?.filter(o => o.poll_id === poll.id) || [];
        const options = pollOptions.map(option => {
          const votes = votesData?.filter(v => v.option_id === option.id).length || 0;
          return {
            id: option.id,
            text: option.text,
            votes
          };
        });

        const totalVotes = options.reduce((sum: number, opt: PollOption) => sum + opt.votes, 0);
        const userVote = votesData?.find(
          v => options.some(o => o.id === v.option_id) && v.user_id === currentUser?.id
        )?.option_id;

        return {
          ...poll,
          options,
          total_votes: totalVotes,
          user_vote: userVote
        };
      });

      setPolls(processedPolls);
    } catch (err) {
      console.error('Error fetching polls:', err);
    }
  };

  const createPoll = async (question: string, options: string[], expiresAt?: Date) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Create poll with category
      const { data: poll, error: pollError } = await supabase
        .from('polls')
        .insert({
          question,
          user_id: user.id,
          expires_at: expiresAt?.toISOString(),
          category_id: selectedCategory,
        })
        .select()
        .single();

      if (pollError) throw pollError;

      // Create poll options
      const optionsData = options.map(text => ({
        poll_id: poll.id,
        text
      }));

      const { error: optionsError } = await supabase
        .from('poll_options')
        .insert(optionsData);

      if (optionsError) throw optionsError;

      await fetchPolls();
      return true;
    } catch (err) {
      console.error('Error creating poll:', err);
      return false;
    }
  };

  const votePoll = async (pollId: string, optionId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('poll_votes')
        .insert({
          user_id: user.id,
          option_id: optionId
        });

      if (error) throw error;

      await fetchPolls();
      return true;
    } catch (err) {
      console.error('Error voting in poll:', err);
      return false;
    }
  };

  const deletePoll = async (pollId: string) => {
    try {
      const { error } = await supabase
        .from('polls')
        .delete()
        .eq('id', pollId);

      if (error) throw error;
      setPolls(prev => prev.filter(p => p.id !== pollId));
      return true;
    } catch (err) {
      console.error('Error deleting poll:', err);
      return false;
    }
  };

  const toggleLike = async (messageId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Determine current like status from DB (authoritative) and local state for counts
      const { data: existingLikes, error: likeCheckError } = await supabase
        .from('message_likes')
        .select('id')
        .eq('message_id', messageId)
        .eq('user_id', user.id);
      if (likeCheckError) throw likeCheckError;

      const isCurrentlyLiked = !!(existingLikes && existingLikes.length > 0);

      // Compute the new like count based on local state
      const current = [...messages, ...pinnedMessages].find(m => m.id === messageId);
      const currentCount = current?.like_count ?? 0;
      const newCount = isCurrentlyLiked ? Math.max(currentCount - 1, 0) : currentCount + 1;

      if (isCurrentlyLiked) {
        // Unlike
        const { error: unlikeError } = await supabase
          .from('message_likes')
          .delete()
          .eq('message_id', messageId)
          .eq('user_id', user.id);
        if (unlikeError) throw unlikeError;

        const { error: decErr } = await supabase
          .from('messages')
          .update({ like_count: newCount })
          .eq('id', messageId);
        if (decErr) throw decErr;
      } else {
        // Like
        const { error: likeErr } = await supabase
          .from('message_likes')
          .insert({ message_id: messageId, user_id: user.id });
        if (likeErr) throw likeErr;

        const { error: incErr } = await supabase
          .from('messages')
          .update({ like_count: newCount })
          .eq('id', messageId);
        if (incErr) throw incErr;
      }

      // Update local state without resetting pagination
      setMessages(prev => prev.map(m => m.id === messageId ? ({ ...m, like_count: newCount, user_has_liked: !isCurrentlyLiked } as any) : m));
      setPinnedMessages(prev => prev.map(m => m.id === messageId ? ({ ...m, like_count: newCount, user_has_liked: !isCurrentlyLiked } as any) : m));
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  useEffect(() => {
    const initializeChat = async () => {
      try {
        setLoading(true);
        
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Authentication required');
        
        setCurrentUser(session.user);
        
        // Set isAdmin based on email - using case-insensitive comparison
        const sessionEmail = session.user.email || '';
        console.log('Current user email:', sessionEmail);
        const isUserAdmin = sessionEmail.toLowerCase() === 'markofilipovic2003@gmail.com'.toLowerCase();
        console.log('Is admin?', isUserAdmin);
        setIsAdmin(isUserAdmin);
        
        // Important: fetch categories first so generalCategoryIdRef is set,
        // then fetch messages/polls using that fallback if no category is selected yet.
        await fetchCategories();
        await Promise.all([fetchMessages(), fetchPolls()]);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Connection error';
        console.error('Error initializing chat:', errorMessage);
        setError(errorMessage === 'Authentication required' 
          ? 'Please sign in to view messages' 
          : 'Connecting to chat...');
        retryFetchMessages();
      } finally {
        setLoading(false);
      }
    };

    initializeChat();

    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [selectedCategory]);

  // Realtime subscription for messages table
  useEffect(() => {
    const subscription = supabase
      .channel('messages')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        async (payload: any) => {
          // Skip if message doesn't match current category filter
          if (selectedCategory && payload.new.category_id !== selectedCategory) {
            return;
          }

          const { data: newMessage } = await supabase
            .from('messages')
            .select(`
              *,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              ),
              comments:comments (
                id,
                content,
                created_at,
                profiles:user_id (
                  email,
                  display_name,
                  avatar_url
                )
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (newMessage) {
            if (newMessage.is_pinned) {
              setPinnedMessages((prev) => [newMessage, ...prev]);
            } else {
              setMessages((prev) => [newMessage, ...prev]);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'messages' },
        (payload: any) => {
          const deletedId = payload.old.id;
          setMessages((prev) => prev.filter((m) => m.id !== deletedId));
          setPinnedMessages((prev) => prev.filter((m) => m.id !== deletedId));
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        async (payload: any) => {
          // Skip if message doesn't match current category filter
          if (selectedCategory && payload.new.category_id !== selectedCategory) {
            return;
          }

          const { data: updatedMessage } = await supabase
            .from('messages')
            .select(`
              *,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              ),
              comments:comments (
                id,
                content,
                created_at,
                profiles:user_id (
                  email,
                  display_name,
                  avatar_url
                )
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (updatedMessage) {
            if (updatedMessage.is_pinned) {
              setMessages((prev) => prev.filter((m) => m.id !== updatedMessage.id));
              setPinnedMessages((prev) => [updatedMessage, ...prev]);
            } else {
              setPinnedMessages((prev) => prev.filter((m) => m.id !== updatedMessage.id));
              setMessages((prev) => [updatedMessage, ...prev]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser?.id, selectedCategory]);

  // Realtime subscription for comments so new comments appear without refresh
  useEffect(() => {
    const subscription = supabase
      .channel('comments')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'comments' },
        async (payload) => {
          const messageId = payload.new.message_id as string;

          // Fetch the updated message including profiles and comments
          const { data: updatedMessage } = await supabase
            .from('messages')
            .select(`
              *,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              ),
              comments:comments (
                id,
                content,
                created_at,
                profiles:user_id (
                  email,
                  display_name,
                  avatar_url
                )
              )
            `)
            .eq('id', messageId)
            .single();

          if (updatedMessage) {
            // Preserve per-user like flag from current state
            setMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? ({ ...updatedMessage, user_has_liked: m.user_has_liked } as any) : m
              )
            );
            setPinnedMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? ({ ...updatedMessage, user_has_liked: m.user_has_liked } as any) : m
              )
            );
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'comments' },
        async (payload) => {
          const messageId = payload.new.message_id as string;
          const { data: updatedMessage } = await supabase
            .from('messages')
            .select(`
              *,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              ),
              comments:comments (
                id,
                content,
                created_at,
                profiles:user_id (
                  email,
                  display_name,
                  avatar_url
                )
              )
            `)
            .eq('id', messageId)
            .single();

          if (updatedMessage) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? ({ ...updatedMessage, user_has_liked: m.user_has_liked } as any) : m
              )
            );
            setPinnedMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? ({ ...updatedMessage, user_has_liked: m.user_has_liked } as any) : m
              )
            );
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'comments' },
        async (payload) => {
          const messageId = payload.old.message_id as string;
          const { data: updatedMessage } = await supabase
            .from('messages')
            .select(`
              *,
              profiles:user_id (
                email,
                display_name,
                avatar_url,
                is_admin
              ),
              comments:comments (
                id,
                content,
                created_at,
                profiles:user_id (
                  email,
                  display_name,
                  avatar_url
                )
              )
            `)
            .eq('id', messageId)
            .single();

          if (updatedMessage) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? ({ ...updatedMessage, user_has_liked: m.user_has_liked } as any) : m
              )
            );
            setPinnedMessages((prev) =>
              prev.map((m) =>
                m.id === messageId ? ({ ...updatedMessage, user_has_liked: m.user_has_liked } as any) : m
              )
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    messages,
    pinnedMessages,
    loading,
    error,
    currentUser,
    isAdmin,
    categories,
    selectedCategory,
    setSelectedCategory,
    togglePin,
    deleteMessage,
    addMessage,
    toggleLike,
    addComment,
    polls,
    createPoll,
    votePoll,
    deletePoll,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
  };
}